/** @format */

import axios, { AxiosRequestConfig } from "axios";
import { API_BASE_URL } from "../../axiosConfig/apiUrl";

/**
 * Phase 2E — task, activity, manual-review service layer.
 *
 * Mirrors the conventions in `collectService.ts` (single-file API client
 * with per-call helpers). Kept separate to avoid further bloating that
 * file. Auth bearer is injected by the global axios interceptor; the
 * tenant header (`x-business-id`) is passed explicitly here.
 */

function withBusiness(
    businessId: string,
    extra: AxiosRequestConfig = {},
): AxiosRequestConfig {
    return {
        ...extra,
        headers: { ...(extra.headers || {}), "x-business-id": businessId },
    };
}

function pickError(error: any, fallback: string): string {
    return error?.response?.data?.message || error?.message || fallback;
}

// ──────────────────────────────────────────────────────────────────────
// Shared shapes (lightweight; full types in collectTypes.ts if needed)
// ──────────────────────────────────────────────────────────────────────

export interface IActivityItem {
    _id: string;
    businessId: string;
    invoiceId?: string;
    customerId?: string;
    type: string;
    severity: "info" | "success" | "warning" | "critical";
    title: string;
    description?: string;
    actorType: "user" | "system" | "ai" | "webhook" | "customer";
    actorUserId?: string;
    actorDisplayName?: string;
    related?: Record<string, string>;
    metadata?: Record<string, any>;
    occurredAt: string;
    createdAt?: string;
}

export interface IActivityPage {
    items: IActivityItem[];
    nextBefore: string | null;
    count: number;
}

export interface ITask {
    _id: string;
    businessId: string;
    invoiceId?: string;
    customerId?: string;
    type: string;
    title: string;
    description?: string;
    priority: "low" | "medium" | "high" | "urgent";
    status: "open" | "in_progress" | "done" | "cancelled";
    dueAt?: string;
    assignedToUserId?:
    | string
    | { _id: string; firstName?: string; lastName?: string; email?: string };
    assignedByUserId?: string;
    assignedAt?: string;
    completedAt?: string;
    completedByUserId?: string;
    completionNote?: string;
    cancellationReason?: string;
    createdByActorType: "user" | "system" | "ai";
    createdByUserId?: string;
    related?: Record<string, string>;
    metadata?: Record<string, any>;
    createdAt?: string;
    updatedAt?: string;
}

export interface ITasksPage {
    items: ITask[];
    total: number;
    limit: number;
    skip: number;
    count: number;
}

export interface ITasksSummary {
    open: number;
    inProgress: number;
    done: number;
    cancelled: number;
    byType: Record<string, number>;
    byPriority: Record<string, number>;
}

export interface IBusinessMember {
    userId: string;
    firstName?: string;
    lastName?: string;
    email?: string;
    profilePicture?: string;
    role: "OWNER" | "ADMIN" | "STAFF";
}

export interface IManualReviewItem {
    _id: string;
    businessId: string;
    docNumber?: string;
    balance: number;
    totalAmount: number;
    currency?: string;
    dueDate?: string;
    daysOverdue?: number;
    priorityScore?: number;
    recoveryState: string;
    recoveryStateEnteredAt?: string;
    customerId?: {
        _id: string;
        displayName?: string;
        primaryEmail?: string;
    };
    assignedToUserId?: {
        _id: string;
        firstName?: string;
        lastName?: string;
        email?: string;
    };
}

export interface IManualReviewPage {
    items: IManualReviewItem[];
    total: number;
    limit: number;
    skip: number;
    count: number;
}

// ──────────────────────────────────────────────────────────────────────
// Activity feed
// ──────────────────────────────────────────────────────────────────────

export interface IListActivityQuery {
    invoiceId?: string;
    customerId?: string;
    types?: string[];
    before?: string;
    limit?: number;
}

export const fetchBusinessActivity = async (
    businessId: string,
    query: IListActivityQuery = {},
): Promise<IActivityPage> => {
    try {
        const params: Record<string, any> = {};
        if (query.invoiceId) params.invoiceId = query.invoiceId;
        if (query.customerId) params.customerId = query.customerId;
        if (query.before) params.before = query.before;
        if (query.limit) params.limit = query.limit;
        if (query.types && query.types.length)
            params.types = query.types.join(",");
        const res = await axios.get(
            `${API_BASE_URL}/collect/activity`,
            withBusiness(businessId, { params }),
        );
        return res.data?.data as IActivityPage;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load activity."));
    }
};

export const fetchInvoiceActivity = async (
    businessId: string,
    invoiceId: string,
    query: Omit<IListActivityQuery, "invoiceId"> = {},
): Promise<IActivityPage> => {
    try {
        const params: Record<string, any> = {};
        if (query.before) params.before = query.before;
        if (query.limit) params.limit = query.limit;
        if (query.types && query.types.length)
            params.types = query.types.join(",");
        const res = await axios.get(
            `${API_BASE_URL}/collect/invoices/${invoiceId}/activity`,
            withBusiness(businessId, { params }),
        );
        return res.data?.data as IActivityPage;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load invoice activity."));
    }
};

// ──────────────────────────────────────────────────────────────────────
// Tasks
// ──────────────────────────────────────────────────────────────────────

export interface IListTasksQuery {
    status?: string[];
    type?: string[];
    priority?: string[];
    assignedToUserId?: string | "me" | "unassigned";
    invoiceId?: string;
    limit?: number;
    skip?: number;
}

export const fetchTasks = async (
    businessId: string,
    query: IListTasksQuery = {},
): Promise<ITasksPage> => {
    try {
        const params: Record<string, any> = {};
        if (query.status?.length) params.status = query.status.join(",");
        if (query.type?.length) params.type = query.type.join(",");
        if (query.priority?.length) params.priority = query.priority.join(",");
        if (query.assignedToUserId)
            params.assignedToUserId = query.assignedToUserId;
        if (query.invoiceId) params.invoiceId = query.invoiceId;
        if (query.limit) params.limit = query.limit;
        if (query.skip) params.skip = query.skip;
        const res = await axios.get(
            `${API_BASE_URL}/collect/tasks`,
            withBusiness(businessId, { params }),
        );
        return res.data?.data as ITasksPage;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load tasks."));
    }
};

export const fetchTasksSummary = async (
    businessId: string,
): Promise<ITasksSummary> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/tasks/summary`,
            withBusiness(businessId),
        );
        return res.data?.data as ITasksSummary;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load task summary."));
    }
};

export const fetchTask = async (
    businessId: string,
    taskId: string,
): Promise<ITask> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/tasks/${taskId}`,
            withBusiness(businessId),
        );
        return res.data?.data as ITask;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load task."));
    }
};

export interface ICreateTaskPayload {
    title: string;
    description?: string;
    type?: string;
    priority?: string;
    dueAt?: string;
    invoiceId?: string;
    customerId?: string;
    assignedToUserId?: string;
}

export const createTask = async (
    businessId: string,
    payload: ICreateTaskPayload,
): Promise<ITask> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/tasks`,
            payload,
            withBusiness(businessId),
        );
        return res.data?.data as ITask;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to create task."));
    }
};

export const claimTaskApi = async (
    businessId: string,
    taskId: string,
): Promise<ITask> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/tasks/${taskId}/claim`,
            {},
            withBusiness(businessId),
        );
        return res.data?.data as ITask;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to claim task."));
    }
};

export const assignTaskApi = async (
    businessId: string,
    taskId: string,
    userId: string,
): Promise<ITask> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/tasks/${taskId}/assign`,
            { userId },
            withBusiness(businessId),
        );
        return res.data?.data as ITask;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to assign task."));
    }
};

export const completeTaskApi = async (
    businessId: string,
    taskId: string,
    note?: string,
): Promise<ITask> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/tasks/${taskId}/complete`,
            { note },
            withBusiness(businessId),
        );
        return res.data?.data as ITask;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to complete task."));
    }
};

export const cancelTaskApi = async (
    businessId: string,
    taskId: string,
    reason?: string,
): Promise<ITask> => {
    try {
        const res = await axios.post(
            `${API_BASE_URL}/collect/tasks/${taskId}/cancel`,
            { reason },
            withBusiness(businessId),
        );
        return res.data?.data as ITask;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to cancel task."));
    }
};

// ──────────────────────────────────────────────────────────────────────
// Manual review queue + business members
// ──────────────────────────────────────────────────────────────────────

export interface IListManualReviewQuery {
    assignedToUserId?: string | "me" | "unassigned";
    limit?: number;
    skip?: number;
}

export const fetchManualReview = async (
    businessId: string,
    query: IListManualReviewQuery = {},
): Promise<IManualReviewPage> => {
    try {
        const params: Record<string, any> = {};
        if (query.assignedToUserId)
            params.assignedToUserId = query.assignedToUserId;
        if (query.limit) params.limit = query.limit;
        if (query.skip) params.skip = query.skip;
        const res = await axios.get(
            `${API_BASE_URL}/collect/manual-review`,
            withBusiness(businessId, { params }),
        );
        return res.data?.data as IManualReviewPage;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load manual review queue."));
    }
};

export const fetchLegalAction = async (
    businessId: string,
    query: IListManualReviewQuery = {},
): Promise<IManualReviewPage> => {
    try {
        const params: Record<string, any> = {};
        if (query.assignedToUserId)
            params.assignedToUserId = query.assignedToUserId;
        if (query.limit) params.limit = query.limit;
        if (query.skip) params.skip = query.skip;
        const res = await axios.get(
            `${API_BASE_URL}/collect/legal-action`,
            withBusiness(businessId, { params }),
        );
        return res.data?.data as IManualReviewPage;
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load legal action queue."));
    }
};

export const fetchBusinessMembers = async (
    businessId: string,
): Promise<IBusinessMember[]> => {
    try {
        const res = await axios.get(
            `${API_BASE_URL}/collect/business/members`,
            withBusiness(businessId),
        );
        return (res.data?.data?.items || []) as IBusinessMember[];
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to load team members."));
    }
};

// Reuses the existing `/collect/invoices/:id/assign` endpoint defined in
// the recovery-queue routes. Body shape: { assigneeUserId: string|null }
export const assignInvoiceReviewer = async (
    businessId: string,
    invoiceId: string,
    assigneeUserId: string | null,
): Promise<void> => {
    try {
        await axios.post(
            `${API_BASE_URL}/collect/invoices/${invoiceId}/assign`,
            { assigneeUserId },
            withBusiness(businessId),
        );
    } catch (e: any) {
        throw new Error(pickError(e, "Failed to assign reviewer."));
    }
};
